/* eslint-disable max-lines */
import React from 'react';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
    Switch
} from 'shared/components/ui/switch';
import {
    cn
} from 'shared/lib/utils';
import InstructionPicker from '../inline-pickers/InstructionPicker';
import FieldHelp from '../components/FieldHelp';
import {
    StepCommonAdvanced, StepCommonInlineFields, StepOelEditor,
    useProcedureStepIds
} from './_shared';
import {
    LlmStepZone, LlmLabeled, LlmFieldLabel, LlmStepCanvas, llmFocusInputClass
} from './llmStepLayout';

const OUTPUT_TYPES = [
    {
        value: 'string',
        label: 'String'
    },
    {
        value: 'dict',
        label: 'Dictionary (JSON object)'
    },
    {
        value: 'list',
        label: 'List'
    },
    {
        value: 'number',
        label: 'Number'
    }
];

const STRUCTURED_OUTPUT_TYPES = new Set(['dict', 'list', 'number']);

const INSTRUCTION_ZONE_HELP = 'Pick a saved Instruction — it defines the model and system prompt. Use Change, Edit, or New to manage Library → Instructions.';

function llmInputOelHelp(oelContext) {
    if (!oelContext?.inFlowScope) {
        return 'Expression evaluated at runtime — usually the user\'s question or assembled context (e.g. @input.query or @step_1.output).';
    }
    const params = (oelContext.subflowParams || []).map((p) => p.name).filter(Boolean);
    const example = params[0] ? `@subflow.${params[0]}` : '@subflow.topic';
    const paramLine = params.length
        ? ` Known With params: ${params.join(', ')}.`
        : ' Add With parameters on the Subflow step that calls this flow, then use @subflow.<name> here.';
    return (
        `Inside a reusable flow: use @subflow.<name> for values passed from the parent Subflow step's With (parameters) — e.g. ${example}.`
        + paramLine
        + ' Use @input.* for opcode-level inputs, or @step_id.output for prior steps in this flow.'
    );
}

function llmInputOelPlaceholder(oelContext) {
    if (!oelContext?.inFlowScope) return '@input.query';
    const first = oelContext.subflowParams?.[0]?.name;
    return first ? `@subflow.${first}` : '@subflow.topic';
}

export default function InlineLLMStep({
    step, onPatchStep, oelContext
}) {
    const inputs = step.inputs || {};
    const stepIdOptions = useProcedureStepIds(oelContext);

    function patch(partial) {
        onPatchStep({
            ...step,
            ...partial
        });
    }
    function patchInputs(partial) {
        onPatchStep({
            ...step,
            inputs: {
                ...inputs,
                ...partial
            }
        });
    }

    return (
        <LlmStepCanvas>
            <LlmStepZone
                variant="configuration"
                title="Configuration"
                description="Choose the brain behind this step and tell it what to read."
            >
                <div className="space-y-1">
                    <LlmFieldLabel
                        label="Instruction"
                        help={INSTRUCTION_ZONE_HELP}
                    />
                    <InstructionPicker
                        showLabel={false}
                        value={{
                            unique_name: step.unique_name,
                            model_type: step.model_type
                        }}
                        onChange={(v) => patch({
                            unique_name: v.unique_name,
                            model_type: v.model_type
                        })}
                    />
                </div>

                <LlmLabeled
                    label="Input (OEL)"
                    help={llmInputOelHelp(oelContext)}
                >
                    <StepOelEditor
                        value={inputs.query || ''}
                        onChange={(v) => patchInputs({
                            query: v
                        })}
                        ctx={oelContext}
                        placeholder={llmInputOelPlaceholder(oelContext)}
                        ariaLabel="LLM input expression"
                    />
                </LlmLabeled>

                <LlmLabeled
                    label="Image URLs (OEL, optional)"
                    help="For vision models: expression resolving to a list of image URLs the model should look at — e.g. @step_1.output.result from a step with List output. The engine downloads each URL and attaches the images to the request. Leave empty for text-only steps."
                >
                    <StepOelEditor
                        value={inputs.image_urls || ''}
                        onChange={(v) => {
                            if (v && v.trim() !== '') {
                                patchInputs({
                                    image_urls: v
                                });
                            } else {
                                const rest = {
                                    ...inputs
                                };
                                delete rest.image_urls;
                                onPatchStep({
                                    ...step,
                                    inputs: rest
                                });
                            }
                        }}
                        ctx={oelContext}
                        placeholder="@step_1.output.result"
                        ariaLabel="LLM image URLs expression"
                    />
                </LlmLabeled>
            </LlmStepZone>

            <LlmStepZone
                variant="output"
                title="Output settings"
                description="Decide how the answer comes back — plain text, structured JSON, or live streaming."
            >
                {step.stream && step.output_type && step.output_type !== 'string' && (
                    <FieldHelp>
                        Streaming only supports <strong>string</strong> output. Turn off stream to use
                        dict/list/number, or change output type to String.
                    </FieldHelp>
                )}
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:items-start">
                    <LlmLabeled
                        label="Output type"
                        help="How the model response is parsed on @step_id.output. String for chat; dict/list for structured JSON. Streaming requires string."
                    >
                        <Select
                            value={step.output_type || 'string'}
                            onValueChange={(v) => patch({
                                output_type: v,
                                ...(STRUCTURED_OUTPUT_TYPES.has(v) ? {
                                    stream: false
                                } : {})
                            })}
                        >
                            <SelectTrigger
                                className={cn('h-8 text-[12.5px]', llmFocusInputClass)}
                                aria-label="LLM output type"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {OUTPUT_TYPES.map(t => (
                                    <SelectItem
                                        key={t.value}
                                        value={t.value}
                                        disabled={step.stream && t.value !== 'string'}
                                    >
                                        {t.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </LlmLabeled>
                    <LlmLabeled
                        label="Stream output"
                        help="Stream partial tokens to the caller (live UI). Only compatible with string output."
                    >
                        <div className={cn(
                            'flex items-center gap-2 rounded-md border px-3 py-2',
                            'border-primary/15 bg-white',
                            step.stream && 'border-primary/30 bg-primary/[0.06]'
                        )}>
                            <Switch
                                checked={!!step.stream}
                                aria-label="LLM stream output"
                                onCheckedChange={(v) => patch({
                                    stream: v,
                                    ...(v ? {
                                        output_type: 'string'
                                    } : {})
                                })}
                            />
                            <span className={cn(
                                'text-sm',
                                step.stream ? 'font-medium text-primary/90' : 'text-muted-foreground'
                            )}>
                                {step.stream ? 'Tokens streamed back to caller' : 'Wait for full response'}
                            </span>
                        </div>
                    </LlmLabeled>
                </div>
            </LlmStepZone>

            <StepCommonInlineFields
                step={step}
                onPatchStep={onPatchStep}
                stepIdOptions={stepIdOptions}
            />

            <StepCommonAdvanced
                step={step}
                onPatchStep={onPatchStep}
                oelContext={oelContext}
            />
        </LlmStepCanvas>
    );
}
